import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

const ParallaxSection = ({ 
  image, 
  children, 
  overlay = true
}: { 
  image: string; 
  children: React.ReactNode;
  overlay?: boolean;
}) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const springConfig = { damping: 15, stiffness: 100 };
  const ySpring = useSpring(y, springConfig);
  const scaleSpring = useSpring(scale, springConfig);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${image}')`,
          y: ySpring,
          scale: scaleSpring
        }}
      >
        {overlay && <div className="absolute inset-0 bg-black/60" />}
      </motion.div>
      <div className="relative z-10 w-full">
        {children}
      </div>
    </div>
  );
};

const ValueCard = ({ emoji, title, description }: { emoji: string; title: string; description: string }) => (
  <motion.div 
    className="relative group"
    whileHover={{ y: -10 }}
    transition={{ duration: 0.3 }}
  >
    <div className="absolute -inset-0.5 bg-gradient-to-r from-[#c9ab81] to-[#9AA581] opacity-20 blur rounded-lg" />
    <div className="relative bg-black/50 p-8 rounded-lg backdrop-blur-sm">
      <div className="text-4xl mb-4">{emoji}</div>
      <h3 className="text-2xl font-serif mb-4 text-[#c9ab81]">{title}</h3>
      <p className="text-white/80 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const FeatureList = ({ items }: { items: string[] | string }) => {
  const itemsArray = Array.isArray(items) ? items : [items];

  return (
    <ul className="space-y-4">
      {itemsArray.map((item, index) => (
        <motion.li 
          key={index}
          className="flex items-start space-x-3 text-white/80"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
        >
          <ArrowRight className="w-5 h-5 text-[#c9ab81] mt-1 flex-shrink-0" />
          <span>{item}</span>
        </motion.li>
      ))}
    </ul>
  );
};

const About = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <ParallaxSection image="https://res.cloudinary.com/dansvy9a5/image/upload/v1739113193/1736490474766_2_owpzsc.jpg">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 
            className="text-6xl md:text-7xl font-serif mb-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('about.hero.title')}
            <br />
            <span className="text-[#c9ab81] italic">{t('about.hero.subtitle')}</span>
          </motion.h1>
        </div>
      </ParallaxSection>

      {/* Introduction Section */}
      <div className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,171,129,0.03)_0%,rgba(0,0,0,0)_70%)] opacity-60" />
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg prose-invert"
          >
            {(t('about.intro.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
              <p key={index} className="text-xl leading-relaxed text-white/80">
                {paragraph}
              </p>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Journey Sections */}
      <div className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4">
          {/* Before Section */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-12 text-center">
              {t('about.journey.before.title')} <span className="text-[#c9ab81] italic">{t('about.journey.before.subtitle')}</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xl text-white/80 mb-8">
                  {t('about.journey.before.description')}
                </p>
                <h3 className="text-2xl font-serif text-[#c9ab81] mb-6">{t('about.journey.before.topics.title')}</h3>
                <FeatureList items={t('about.journey.before.topics.items', { returnObjects: true })} />
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="relative aspect-[3/4] rounded-lg overflow-hidden"
              >
                <img 
                  src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=3000&auto=format&fit=crop"
                  alt="Preparation for immersion"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </motion.div>
            </div>
          </motion.div>

          {/* During Section */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-12 text-center">
              {t('about.journey.during.title')} <span className="text-[#c9ab81] italic">{t('about.journey.during.subtitle')}</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="relative aspect-[3/4] rounded-lg overflow-hidden lg:order-2"
              >
                <img 
                  src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=3000&auto=format&fit=crop"
                  alt="During immersion"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </motion.div>
              <div className="lg:order-1">
                <p className="text-xl text-white/80 mb-8">
                  {t('about.journey.during.description')}
                </p>
                <div className="bg-[#9AA581]/10 p-8 rounded-lg">
                  <h3 className="text-2xl font-serif text-[#c9ab81] mb-6">{t('about.journey.during.presence.title')}</h3>
                  <p className="text-white/80 leading-relaxed">
                    {t('about.journey.during.presence.description')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* After Section */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif mb-12 text-center">
              {t('about.journey.after.title')} <span className="text-[#c9ab81] italic">{t('about.journey.after.subtitle')}</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="space-y-8">
                <h3 className="text-2xl font-serif text-[#c9ab81]">{t('about.journey.after.followUp.title')}</h3>
                <FeatureList items={t('about.journey.after.followUp.items', { returnObjects: true })} />
              </div>
              <div className="space-y-8">
                <h3 className="text-2xl font-serif text-[#c9ab81]">{t('about.journey.after.community.title')}</h3>
                <FeatureList items={t('about.journey.after.community.items', { returnObjects: true })} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Transformation Results */}
      <div className="py-20 bg-[#9AA581]/5">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif mb-6">
              {t('about.transformation.title')} <span className="text-[#c9ab81] italic">{t('about.transformation.subtitle')}</span>
            </h2>
          </motion.div>
          <div className="space-y-4">
            <FeatureList items={t('about.transformation.items', { returnObjects: true })} />
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,171,129,0.03)_0%,rgba(0,0,0,0)_70%)] opacity-60" />
        </div>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            className="space-y-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-serif text-center mb-12 text-white">
              {t('about.values.title')} <span className="text-[#c9ab81] italic">{t('about.values.subtitle')}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ValueCard 
                emoji="🌿"
                title={t('about.values.cards.authenticity.title')}
                description={t('about.values.cards.authenticity.description')}
              />
              <ValueCard 
                emoji="🧡"
                title={t('about.values.cards.caring.title')}
                description={t('about.values.cards.caring.description')}
              />
              <ValueCard 
                emoji="🔥"
                title={t('about.values.cards.empowerment.title')}
                description={t('about.values.cards.empowerment.description')}
              />
              <ValueCard 
                emoji="🌊"
                title={t('about.values.cards.harmony.title')}
                description={t('about.values.cards.harmony.description')}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="relative py-32 overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113190/1736183405038_2_rfj0d1.jpg')"
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-serif mb-8">
              {t('about.cta.title')} <span className="text-[#c9ab81] italic">{t('about.cta.before')}</span> {t('about.cta.and')} <span className="text-[#c9ab81] italic">{t('about.cta.after')}</span>.
            </h2>
            <p className="text-xl text-white/80 mb-12">
              {t('about.cta.description')}
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSc5mmPDqzUeexziQ_G5pmpiTNH2flL2p6DW5yWL2cu1lWC-6Q/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-12 py-4 bg-[#c9ab81] text-black font-serif text-lg hover:bg-white transition-colors duration-300 rounded"
              >
                {t('about.cta.button')}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;